import type { DAOutcome } from '@/lib/api/med-requests/decision-assistant'
import { computeShowDecisionAssistant, isPomMedRequest } from '@/hooks/useDecisionAssistantVisibility'
import { isRoleAllowedToUseDecisionAssistant } from '@/lib/permissions'
import { queryClient } from '@/services/queryClient'
import type { DecisionAssistantAnalytics } from '@/types/posthog'
import type { MedRequest } from '@/types/data'

type AnalyticsContext = {
  daFlagEnabled: boolean
  patientOrigin?: 'medication-request' | 'investigation'
  isInvestigation?: boolean
}

function getMedRequest(medReqId: string): MedRequest | undefined {
  return queryClient.getQueryData<MedRequest>(['medicationRequestById', medReqId])
}

function getDaShown(medReqId: string, context: AnalyticsContext): boolean {
  const medRequest = getMedRequest(medReqId)
  const hasOnHoldHistory = Array.isArray(medRequest?.onHoldHistory) && medRequest.onHoldHistory.length > 0

  return computeShowDecisionAssistant({
    daFlagEnabled: context.daFlagEnabled,
    patientOrigin: context.patientOrigin,
    isInvestigation: context.isInvestigation,
    hasOnHoldHistory,
    hasDaAccessRole: isRoleAllowedToUseDecisionAssistant(),
    isPomOrder: isPomMedRequest(medRequest)
  })
}

export function getDecisionAssistantViewAnalytics(
  medReqId: string,
  context: AnalyticsContext
): DecisionAssistantAnalytics {
  return {
    da_flag_enabled: context.daFlagEnabled,
    da_shown: getDaShown(medReqId, context),
    treatment: getMedRequest(medReqId)?.orderedItem.category
  }
}

export function getDecisionAssistantActionAnalytics(
  medReqId: string,
  context: AnalyticsContext
): DecisionAssistantAnalytics {
  const da_shown = getDaShown(medReqId, context)
  const treatment = getMedRequest(medReqId)?.orderedItem.category

  if (!da_shown) {
    return { da_flag_enabled: context.daFlagEnabled, da_shown, treatment }
  }

  const daQuery = queryClient.getQueryState<DAOutcome>(['decisionAssistant', medReqId])
  const da_outcome = daQuery?.data?.type ?? null
  const da_available = !(daQuery?.status === 'error' && daQuery.data === undefined)

  return {
    da_flag_enabled: context.daFlagEnabled,
    da_shown,
    da_available,
    da_outcome,
    treatment
  }
}
